/**
 * The content schema shared by the authoring DSL, the game UI and the Rust
 * acceptance tests.
 *
 * `build.ts` compiles it into two files: [`LevelFile`] to
 * `generated/levels.json`, which the browser reads, and [`SolutionFile`] to
 * `generated/solutions.json`, which only the acceptance tests read. Together
 * they are the single source of truth for what a level asks for and how a law
 * is checked.
 */

/** Comparison mode, mirroring `lc_core::Mode`. */
export type Mode =
  | { kind: 'eq' }
  | { kind: 'eq_eta' }
  | { kind: 'distinct' };

/**
 * How the quantified variables of a case were instantiated.
 *
 * `universal` means every variable was an opaque atom, so the equation was
 * decided with an arbitrary term in each place: a law, not a sample.
 * `enumerated` means the case was one of the values the level is about.
 */
export type ProofKind = 'universal' | 'enumerated';

/** Per-level resource limits, mirroring `lc_core::Budget`. */
export interface Budget {
  maxSteps?: number;
  maxWork?: number;
  maxSize?: number;
  maxDepth?: number;
}

/**
 * The largest budget the engine will accept from the browser.
 *
 * This mirrors `BUDGET_CEILING` in `crates/lc-wasm`: the boundary clamps every
 * request to it, so a level that asked for more would be checked with less
 * than it says and behave differently in the browser than in the tests.
 */
export const BUDGET_CEILING: Required<Budget> = {
  maxSteps: 3_000_000,
  maxWork: 200_000_000,
  maxSize: 1_000_000,
  maxDepth: 16_384,
};

/**
 * The budget a level is checked with, and the sandbox reduces with.
 *
 * One number for the whole game, set from the heaviest case the content
 * contains: fifteen times what that case costs, which is the room a correct
 * answer is given to be slower than the reference before the checker stops and
 * calls the case undecided. The acceptance suite fails if any declared answer
 * needs more than a tenth of it, and if the heaviest case falls far enough
 * below it that the number stops saying anything.
 *
 * It bounds time, not correctness: a term that runs out of it is reported as
 * "could not decide", never as wrong, and a definition that never terminates
 * stops in well under a second, which is what the limit is really for.
 *
 * The depth is the same ceiling the WebAssembly boundary enforces, so a bigger
 * number here would be silently reduced.
 */
export const LIMITS: Budget = {
  maxSteps: 3_000_000,
  // The work budget bounds the engine's own bookkeeping, which is charged for
  // every step it takes: the heaviest reference answer costs about 1_400_000 of
  // it, and an answer that unfolds forever reaches ten million in half a
  // second, where the depth limit used to take ten.
  maxWork: 10_000_000,
  maxSize: 800_000,
  maxDepth: 16_384,
};

/** One concrete equation to check. */
export interface CaseSpec {
  id: string;
  label: string;
  lhs: string;
  rhs: string;
  /**
   * Further terms, for a mode that compares more than two of them. A
   * distinctness case lists its terms as `lhs`, `rhs`, then these.
   */
  also?: string[];
  mode: Mode;
  proofKind: ProofKind;
}

/** A name the player must define, and the shape the interface shows for it. */
export interface Slot {
  name: string;
  signature: string;
}

/** A level: a statement, the names it needs, and the laws it checks. */
export interface Level {
  id: string;
  title: string;
  body: string;
  requires: string[];
  slots: Slot[];
  cases: CaseSpec[];
  limits?: Budget;
  /**
   * A page to read rather than a level to solve.
   *
   * A lesson has no names to define and nothing to check: it says something
   * about the language itself, which belongs to every level and to none of
   * them. The interface shows its body and no editor.
   */
  lesson?: boolean;
}

/** A group of levels. */
export interface Chapter {
  id: string;
  title: string;
  levels: Level[];
}

/**
 * A deliberately wrong solution that must be rejected by its level's cases.
 * Mutants are what keep the tests honest: if a mutant passes, the level does
 * not actually constrain the answer.
 */
export interface Mutant {
  id: string;
  level: string;
  definitions: Record<string, string>;
  reason: string;
}

/**
 * The public half of the compiled content: everything the player may see.
 * It is served to the browser and deliberately contains no solutions.
 */
export interface LevelFile {
  version: number;
  chapters: Chapter[];
}

/**
 * The private half: reference solutions and mutants. It is read only by the
 * acceptance tests, never by the application, so no answer can leak into the
 * shipped bundle.
 */
export interface SolutionFile {
  version: number;
  /** Reference solutions: level id to definition name to term source. */
  solutions: Record<string, Record<string, string>>;
  alternatives: Alternative[];
  mutants: Mutant[];
}

/**
 * A correct answer to a level, written a different way.
 *
 * Cases have to accept every right answer, not only the reference one, and the
 * ways of being right are hard to guess in advance: a closed form where the
 * reference recurses, a general combinator that happens to be the function, or
 * a composition of library functions. Each of these has to pass, which is
 * also what stops a level's budget from quietly deciding which answers count.
 */
export interface Alternative {
  id: string;
  level: string;
  /** The definitions that differ from the reference solution. */
  definitions: Record<string, string>;
  /** How this answer differs, and why it is still correct. */
  reason: string;
}

/**
 * A level bundled with everything that must stay in sync with it: the
 * reference solution that proves it is solvable, and the mutants that prove
 * its cases actually reject wrong answers.
 *
 * The mutants and the alternatives do not carry ids: the compiler names them
 * `level/m1`, `level/m2`, … and `level/a1`, `level/a2`, …, in the order they
 * are written, so the chapters cannot drift from the scheme and the ids carry
 * no information that has to be kept up to date by hand.
 */
export interface LevelDef {
  level: Level;
  solutions: Record<string, string>;
  alternatives?: Omit<Alternative, 'id' | 'level'>[];
  mutants?: Omit<Mutant, 'id' | 'level'>[];
}
